import { degreesToRadians } from './utilities';
import { cubieEdgeLength } from '../components/Cube';

// Cube body-frame axes used by this application:
// x: left (-) / right (+), y: top (-) / bottom (+), z: back (-) / front (+).
const FACE_NORMALS = {
    U: [0, -1, 0],
    D: [0, 1, 0],
    F: [0, 0, 1],
    B: [0, 0, -1],
    L: [-1, 0, 0],
    R: [1, 0, 0]
};

// The order is intentionally the same order requested by the Decision Engine.
// The cubie indexes match createSolvedCubieCoordinates in CubeContainer.js.
const CORNER_DEFINITIONS = [
    { cubieIndex: 23, position: [-1, -1, 1], faces: ['U', 'F', 'L'] }, // UFL
    { cubieIndex: 14, position: [-1, -1, -1], faces: ['U', 'L', 'B'] }, // UBL
    { cubieIndex: 16, position: [1, -1, -1], faces: ['U', 'B', 'R'] }, // UBR
    { cubieIndex: 25, position: [1, -1, 1], faces: ['U', 'R', 'F'] }, // UFR
    { cubieIndex: 24, position: [-1, 1, 1], faces: ['D', 'L', 'F'] }, // DFL
    { cubieIndex: 15, position: [-1, 1, -1], faces: ['D', 'B', 'L'] }, // DBL
    { cubieIndex: 17, position: [1, 1, -1], faces: ['D', 'R', 'B'] }, // DBR
    { cubieIndex: 26, position: [1, 1, 1], faces: ['D', 'F', 'R'] } // DFR
];

const EDGE_DEFINITIONS = [
    { cubieIndex: 21, position: [0, -1, 1], faces: ['U', 'F'] }, // UF
    { cubieIndex: 7, position: [1, -1, 0], faces: ['U', 'R'] }, // UR
    { cubieIndex: 12, position: [0, -1, -1], faces: ['U', 'B'] }, // UB
    { cubieIndex: 5, position: [-1, -1, 0], faces: ['U', 'L'] }, // UL
    { cubieIndex: 22, position: [0, 1, 1], faces: ['D', 'F'] }, // DF
    { cubieIndex: 8, position: [1, 1, 0], faces: ['D', 'R'] }, // DR
    { cubieIndex: 13, position: [0, 1, -1], faces: ['D', 'B'] }, // DB
    { cubieIndex: 6, position: [-1, 1, 0], faces: ['D', 'L'] }, // DL
    { cubieIndex: 20, position: [1, 0, 1], faces: ['F', 'R'] }, // FR
    { cubieIndex: 19, position: [-1, 0, 1], faces: ['F', 'L'] }, // FL
    { cubieIndex: 11, position: [1, 0, -1], faces: ['B', 'R'] }, // BR
    { cubieIndex: 10, position: [-1, 0, -1], faces: ['B', 'L'] } // BL
];

const POSITION_TOLERANCE = .01;
const ORIENTATION_DOT_TOLERANCE = .999;

/**
 * Convert the live visual cube into a view-independent discrete state.
 *
 * Corner order: UFL, UBL, UBR, UFR, DFL, DBL, DBR, DFR.
 * Edge order:   UF, UR, UB, UL, DF, DR, DB, DL, FR, FL, BR, BL.
 *
 * Returns null while a layer is between snapped quarter-turn positions or
 * when the visual state cannot be interpreted as a legal cubie arrangement.
 */
export const getDiscreteCubeState = (
    cubieCoordinates,
    cubieRotationAxes,
    cubieRotationDegrees
) => {
    if (!isCompleteVisualState(
        cubieCoordinates,
        cubieRotationAxes,
        cubieRotationDegrees
    )) {
        return null;
    }

    const viewMatrix = axisAngleToMatrix(
        cubieRotationAxes[0],
        cubieRotationDegrees[0]
    );
    const inverseViewMatrix = transposeMatrix(viewMatrix);

    const bodyPositions = cubieCoordinates.map(position =>
        multiplyMatrixVector(inverseViewMatrix, position)
    );
    const bodyOrientations = cubieRotationAxes.map((axis, cubieIndex) => {
        const worldOrientation = axisAngleToMatrix(
            axis,
            cubieRotationDegrees[cubieIndex]
        );
        return multiplyMatrices(inverseViewMatrix, worldOrientation);
    });

    const corners = readPieceGroup(
        CORNER_DEFINITIONS,
        bodyPositions,
        bodyOrientations,
        3
    );
    const edges = readPieceGroup(
        EDGE_DEFINITIONS,
        bodyPositions,
        bodyOrientations,
        2
    );

    if (!corners || !edges) {
        return null;
    }

    const isSolved =
        corners.every((corner, slotIndex) =>
            corner.piece === slotIndex && corner.ori === 0
        ) &&
        edges.every((edge, slotIndex) =>
            edge.piece === slotIndex && edge.ori === 0
        );

    return { corners, edges, isSolved };
};

const isCompleteVisualState = (coordinates, axes, degrees) => (
    coordinates && axes && degrees &&
    coordinates.length === 27 &&
    axes.length === 27 &&
    degrees.length === 27
);

const readPieceGroup = (
    definitions,
    bodyPositions,
    bodyOrientations,
    requiredNonZeroCoordinates
) => {
    const pieceBySlotKey = {};

    for (let pieceId = 0; pieceId < definitions.length; pieceId++) {
        const definition = definitions[pieceId];
        const quantizedPosition = quantizeBodyPosition(
            bodyPositions[definition.cubieIndex]
        );

        if (!quantizedPosition ||
            countNonZeroCoordinates(quantizedPosition) !== requiredNonZeroCoordinates) {
            return null;
        }

        const slotKey = positionKey(quantizedPosition);
        if (pieceBySlotKey[slotKey] !== undefined) {
            return null;
        }
        pieceBySlotKey[slotKey] = pieceId;
    }

    const result = new Array(definitions.length);

    for (let slotId = 0; slotId < definitions.length; slotId++) {
        const slotDefinition = definitions[slotId];
        const pieceId = pieceBySlotKey[positionKey(slotDefinition.position)];

        if (pieceId === undefined) {
            return null;
        }

        const pieceDefinition = definitions[pieceId];
        const referenceStickerNormal = FACE_NORMALS[pieceDefinition.faces[0]];
        const rotatedReferenceNormal = multiplyMatrixVector(
            bodyOrientations[pieceDefinition.cubieIndex],
            referenceStickerNormal
        );
        const slotFaceNormals = slotDefinition.faces.map(faceName =>
            FACE_NORMALS[faceName]
        );
        const orientation = findAlignedFaceIndex(
            rotatedReferenceNormal,
            slotFaceNormals
        );

        if (orientation === null) {
            return null;
        }

        result[slotId] = { piece: pieceId, ori: orientation };
    }

    return result;
};

const quantizeBodyPosition = position => {
    if (!position || position.length !== 3) {
        return null;
    }

    const quantized = [];
    for (let axisIndex = 0; axisIndex < 3; axisIndex++) {
        const normalizedCoordinate = position[axisIndex] / cubieEdgeLength;
        const nearestGridCoordinate = Math.round(normalizedCoordinate);

        if (!isFinite(normalizedCoordinate) ||
            nearestGridCoordinate < -1 || nearestGridCoordinate > 1 ||
            Math.abs(normalizedCoordinate - nearestGridCoordinate) > POSITION_TOLERANCE) {
            return null;
        }

        quantized.push(nearestGridCoordinate === 0 ? 0 : nearestGridCoordinate);
    }

    return quantized;
};

const countNonZeroCoordinates = position => position.reduce(
    (count, coordinate) => count + (coordinate === 0 ? 0 : 1),
    0
);

const positionKey = position => position.join(',');

const findAlignedFaceIndex = (vector, candidateNormals) => {
    const normalizedVector = normalizeVector(vector);
    let bestIndex = -1;
    let bestDotProduct = -Infinity;

    for (let candidateIndex = 0;
        candidateIndex < candidateNormals.length;
        candidateIndex++) {
        const dotProduct = dot(normalizedVector, candidateNormals[candidateIndex]);
        if (dotProduct > bestDotProduct) {
            bestDotProduct = dotProduct;
            bestIndex = candidateIndex;
        }
    }

    return bestDotProduct >= ORIENTATION_DOT_TOLERANCE ? bestIndex : null;
};

const normalizeVector = vector => {
    const length = Math.sqrt(dot(vector, vector));
    if (!isFinite(length) || length < .0000001) {
        return [0, 0, 0];
    }
    return vector.map(component => component / length);
};

const dot = (first, second) =>
    first[0] * second[0] +
    first[1] * second[1] +
    first[2] * second[2];

const axisAngleToMatrix = (axis, degrees) => {
    let normalizedAxis = normalizeVector(axis || [1, 0, 0]);
    if (dot(normalizedAxis, normalizedAxis) < .5) {
        normalizedAxis = [1, 0, 0];
    }
    const angleRadians = degreesToRadians(isFinite(degrees) ? degrees : 0);
    const cosine = Math.cos(angleRadians);
    const sine = Math.sin(angleRadians);
    const oneMinusCosine = 1 - cosine;
    const x = normalizedAxis[0];
    const y = normalizedAxis[1];
    const z = normalizedAxis[2];

    return [
        [
            cosine + x * x * oneMinusCosine,
            x * y * oneMinusCosine - z * sine,
            x * z * oneMinusCosine + y * sine
        ],
        [
            y * x * oneMinusCosine + z * sine,
            cosine + y * y * oneMinusCosine,
            y * z * oneMinusCosine - x * sine
        ],
        [
            z * x * oneMinusCosine - y * sine,
            z * y * oneMinusCosine + x * sine,
            cosine + z * z * oneMinusCosine
        ]
    ];
};

const transposeMatrix = matrix => [
    [matrix[0][0], matrix[1][0], matrix[2][0]],
    [matrix[0][1], matrix[1][1], matrix[2][1]],
    [matrix[0][2], matrix[1][2], matrix[2][2]]
];

const multiplyMatrixVector = (matrix, vector) => [
    matrix[0][0] * vector[0] + matrix[0][1] * vector[1] + matrix[0][2] * vector[2],
    matrix[1][0] * vector[0] + matrix[1][1] * vector[1] + matrix[1][2] * vector[2],
    matrix[2][0] * vector[0] + matrix[2][1] * vector[1] + matrix[2][2] * vector[2]
];

const multiplyMatrices = (left, right) => [
    [
        left[0][0] * right[0][0] + left[0][1] * right[1][0] + left[0][2] * right[2][0],
        left[0][0] * right[0][1] + left[0][1] * right[1][1] + left[0][2] * right[2][1],
        left[0][0] * right[0][2] + left[0][1] * right[1][2] + left[0][2] * right[2][2]
    ],
    [
        left[1][0] * right[0][0] + left[1][1] * right[1][0] + left[1][2] * right[2][0],
        left[1][0] * right[0][1] + left[1][1] * right[1][1] + left[1][2] * right[2][1],
        left[1][0] * right[0][2] + left[1][1] * right[1][2] + left[1][2] * right[2][2]
    ],
    [
        left[2][0] * right[0][0] + left[2][1] * right[1][0] + left[2][2] * right[2][0],
        left[2][0] * right[0][1] + left[2][1] * right[1][1] + left[2][2] * right[2][1],
        left[2][0] * right[0][2] + left[2][1] * right[1][2] + left[2][2] * right[2][2]
    ]
];
